<template>
  <div class="visitor-data view row" :class="{'visitor-data--pending': pending}">
    <div class="col-12">
      <router-link :to="['/online-appointment', locationId, appointmentTypeId, date].join('/')">
        <i class="fas fa-chevron-left" aria-hidden="true"></i>
        {{ $t('navigation.back') }}
      </router-link>
    </div>
    <div class="col-12 mt-3">

      <div class="text-uppercase h1 font-weight-light">{{ localizedDay }}</div>

      <form-input
          type="date"
          class="large-date-control"
          disabled
          required
          :value="inputFormattedDate"
      />

      <div class="my-3 h2">
        {{ toFormattedDate(date, $t('visitor_data.appointment_full_date_format')) }}
      </div>

      <div v-if="pending" class="pending-overlay">
        <img src="@/assets/loading.svg" class="img-fluid sticky-top" alt="">
      </div>


      <form v-else slot="content" class="pb-4" @submit.prevent="onSubmit">
        <form-input class="mb-2"
                    v-model="appointment.first_name"
                    required
                    name="first_name"
                    :invalid-note="$t('visitor_data.first_name.invalid_note')"
                    :label="$t('visitor_data.first_name.label')"/>

        <form-input class="mb-2"
                    v-model="appointment.last_name"
                    name="last_name"
                    required
                    :invalid-note="$t('visitor_data.last_name.invalid_note')"
                    :label="$t('visitor_data.last_name.label')"
        />

        <form-input
            class="mb-2"
            type="date"
            name="date_of_birth"
            required
            :label="$t('visitor_data.date_of_birth.label')"
            :invalid-note="$t('visitor_data.date_of_birth.invalid_note')"
            v-model="appointment.date_of_birth"
        />

        <form-input class="mb-2"
                    name="phone"
                    required
                    type="phone"
                    v-model="appointment.phone"
                    :invalid-note="$t('visitor_data.phone.invalid_note')"
                    :label="$t('visitor_data.phone.label')"
        />

        <form-input class="mb-2"
                    name="email"
                    type="email"
                    v-model="appointment.email"
                    :invalid-note="$t('visitor_data.email.invalid_note')"
                    :label="$t('visitor_data.email.label')"
        />

        <form-select
            class="mb-2"
            :options="insuranceOptions"
            option-value-key="key"
            option-label-key="label"
            :label="$t('visitor_data.insurance.label')"
            :invalid-note="$t('visitor_data.insurance.invalid_note')"
            v-model="appointment.insurance"
        />

        <div class="form-check my-3">
          <input v-model="appointment.check_privacy"
                 class="form-check-input"
                 type="checkbox"
                 required
                 id="privacy1"
          >
          <label class="form-check-label"
                 for="privacy1"
                 v-html="$t('visitor_data.label_privacy', $config)"
          />
        </div>

        <button class="btn-lg btn btn-primary btn-block my-4 font-weight-light">
          {{ $t('visitor_data.register') }}
        </button>

      </form>


    </div>
  </div>
</template>

<script>

import {mapActions, mapMutations, mapState} from 'vuex';

import FormInput         from 'pixelstein-vue-app-package/src/vue2/PsForm/PsFormInput';
import FormSelect        from 'pixelstein-vue-app-package/src/vue2/PsForm/PsFormSelect';
import FormDateTimeInput from '@/components/FormDateTimeInput';
import dayjs             from 'dayjs';
import loginOptions      from 'pixelstein-vue-app-package/src/mixins/login-options';

import dateFormats from 'pixelstein-vue-app-package/src/mixins/date-formats';

export default {
  name: 'VisitorData',
  mixins: [loginOptions, dateFormats],
  components: {FormDateTimeInput, FormInput, FormSelect},
  props: {
    locationId: {type: String, default: null},
    date: {type: String, default: null},
    appointmentTypeId: {type: String, default: null},
  },
  data() {
    return {
      appointment: {
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        insurance: '',
        check_privacy: false,
      },
      pending: false,
    };
  },
  computed: {
    ...mapState({
      appointmentTypes: state => state.Api.AppointmentTypes.all,
      locations: state => state.Api.Locations.all,
    }),
    insuranceOptions() {
      return [
        {
          key: 'private',
          label: this.$t('visitor_data.insuranceOptions.private'),
        },
        {
          key: 'statutory',
          label: this.$t('visitor_data.insuranceOptions.statutory'),
        },
      ];
    },
    selectedAppointmentType() {
      return this.appointmentTypes.find(({id}) => id === this.appointmentTypeId) ?? null;
    },
    duration() {
      return this.selectedAppointmentType?.duration ?? 15;
    },
    appointmentType() {
      return this.selectedAppointmentType?.name;
    },
    localizedDay() {
      return dayjs(this.date).format('dddd');
    },
    inputFormattedDate() {
      return dayjs(this.date).format(this.$config.API_DATE_FORMAT);
    },
    appointmentEnd() {
      return dayjs(this.date).add(this.duration, 'minutes');
    },
  },
  methods: {
    ...mapActions({
      registerAppointment: 'Api/Appointments/register',
    }),
    ...mapMutations({
      setAppointment: 'Api/Appointments/setCurrent',
    }),
    async onSubmit() {
      if (!this.selectedAppointmentType) {
        this.$router.replace(['/online-appointment', this.locationId].join('/'));
        return;
      }
      if (this.pending) {
        return;
      }
      this.pending = true;

      try {
        const location = this.locations.find(({id}) => id === this.locationId);
        const webAddress = new URL(location.web);
        webAddress.searchParams.append('booking_code', '__BOOKING_CODE__');

        const appointment = await this.registerAppointment({
          ...this.appointment,
          location_id: this.locationId,
          start: dayjs(this.date).format(this.$config.API_DATE_TIME_FORMAT),
          end: dayjs(this.appointmentEnd).format(this.$config.API_DATE_TIME_FORMAT),
          cancel_url: `${window.location.origin}/cancel/__BOOKING_CODE__`,
          appointment_url: webAddress.toString(),
          appointment_type: this.appointmentType
        });

        this.setAppointment({item: appointment});

        this.$router.push(
          ['/online-appointment', this.locationId, this.appointmentTypeId, appointment.id, 'thank-you'].join('/'))
            .catch(console.log);

      } catch (e) {
        console.error(e);
        this.$apiErrorHandler(e);
      }
      this.pending = false;
    },
  },
};
</script>

